import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowRight, Microphone, Waveform, CardsThree, BookOpen, Timer, GraduationCap, Headphones, Car, PersonSimpleWalk } from "@phosphor-icons/react";

// Rotating examples — one student, one professional, one daily-life
const EXAMPLES = [
  {
    audience: "Student · Biology 201",
    meta: "LIVE SESSION — CHUNK 07 / 24",
    line: "The mitochondria produces ATP through cellular respiration in the cristae.",
    score: "92%",
  },
  {
    audience: "Professional · Q4 Board Deck",
    meta: "LIVE SESSION — CHUNK 03 / 12",
    line: "The three pillars of the Q4 strategy are retention, margin, and reinvestment.",
    score: "94%",
  },
  {
    audience: "Student · US History",
    meta: "LIVE SESSION — CHUNK 12 / 48",
    line: "The Treaty of Versailles was signed in 1919, ending World War I.",
    score: "88%",
  },
  {
    audience: "Professional · Sales Pitch",
    meta: "LIVE SESSION — CHUNK 02 / 09",
    line: "Our platform cuts onboarding time from six weeks to nine days.",
    score: "90%",
  },
];

export default function Landing() {
  const [exIdx, setExIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setExIdx((i) => (i + 1) % EXAMPLES.length), 4500);
    return () => clearInterval(id);
  }, []);
  const ex = EXAMPLES[exIdx];

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />

      {/* HERO */}
      <section className="relative">
        <div className="paper-grid absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-28 fade-in">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-3 tracking-[0.3em]" data-testid="hero-phonetic">[ EH&nbsp;·&nbsp;KOL ]</div>
              <div className="eyebrow mb-6" data-testid="hero-eyebrow">Voice-first memorization · For students &amp; professionals</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="hero-heading">
                Hear it.<br/>
                Say it.<br/>
                <span className="italic text-[color:var(--oxford)]">Know it.</span>
              </h1>
              <p className="mt-8 text-lg leading-relaxed max-w-xl text-[color:var(--ink-muted)]" data-testid="hero-subcopy">
                Ekkol reads your material aloud, listens while you repeat, and scores every
                attempt. Speak it into memory — on a walk, in the car as a passenger, or at your desk.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/login" className="btn-primary" data-testid="hero-cta-primary">
                  Begin a deck <ArrowRight size={18} weight="bold" />
                </Link>
                <a href="#how" className="btn-ghost" data-testid="hero-cta-secondary">
                  How it works
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5 text-xs text-[color:var(--ink-muted)] font-mono">
                <span className="inline-flex items-center gap-2"><Headphones size={14}/> Hands-free mode</span>
                <span className="inline-flex items-center gap-2"><Car size={14}/> Practice on the go</span>
                <span className="inline-flex items-center gap-2"><PersonSimpleWalk size={14}/> Walk &amp; study</span>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="border rule bg-white p-8 md:p-10 relative overflow-hidden" data-testid="hero-live-card">
                <div className="eyebrow" key={`meta-${exIdx}`} style={{animation:"fadeIn 400ms ease"}}>{ex.meta}</div>
                <div className="text-[11px] font-mono text-[color:var(--oxford)] mt-1 tracking-[0.15em]">{ex.audience.toUpperCase()}</div>
                <p key={`line-${exIdx}`} className="font-serif text-2xl md:text-3xl leading-snug mt-4 fade-in">
                  &ldquo;{ex.line}&rdquo;
                </p>
                <div className="flex items-end justify-between mt-8 pt-6 border-t rule">
                  <div>
                    <div className="eyebrow">Match score</div>
                    <div key={`score-${exIdx}`} className="font-mono text-3xl mt-1 fade-in">{ex.score}</div>
                  </div>
                  <div className="wave"><span style={{height:14}}/><span style={{height:30}}/><span style={{height:22}}/><span style={{height:40}}/><span style={{height:18}}/><span style={{height:34}}/><span style={{height:12}}/></div>
                  <div className="record-btn active" aria-hidden>
                    <Microphone size={28} weight="fill" />
                  </div>
                </div>
                <div className="flex justify-center gap-1.5 mt-6">
                  {EXAMPLES.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setExIdx(i)}
                      aria-label={`Example ${i+1}`}
                      className={`h-1 transition-all ${i===exIdx ? "w-8 bg-[color:var(--ink)]" : "w-3 bg-[color:var(--ink-faint)]"}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HANDS-FREE highlight */}
      <section className="border-t rule bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow mb-4">Hands-free mode</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Practice on the go.<br/>
              Rehearse while you walk.
            </h2>
            <p className="mt-6 text-[color:var(--ink-muted)] leading-relaxed max-w-lg">
              Put on headphones and press start. Ekkol reads a section, waits for you to
              repeat it, scores the match, and moves on — all without you touching the screen.
              Say <span className="font-mono text-xs">&quot;repeat&quot;</span> or
              <span className="font-mono text-xs"> &quot;next&quot;</span> to override anytime.
            </p>
          </div>
          <div className="border rule p-8 grid grid-cols-3 gap-px bg-[color:var(--rule)]">
            {[
              { n: "01", t: "Reads", d: "Chunk plays aloud" },
              { n: "02", t: "Listens", d: "Mic opens after" },
              { n: "03", t: "Scores", d: "Auto-advance ≥ threshold" },
            ].map((s) => (
              <div key={s.n} className="bg-white p-6 flex flex-col">
                <div className="font-mono text-xs text-[color:var(--ink-faint)]">{s.n}</div>
                <div className="font-serif text-2xl mt-2">{s.t}</div>
                <div className="text-xs text-[color:var(--ink-muted)] mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="how" className="border-t rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="eyebrow mb-4">The method</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl">
            Repetition is the engine of learning. We built the vehicle.
          </h2>

          <div className="grid md:grid-cols-3 gap-px mt-16 bg-[color:var(--rule)] border rule">
            {[
              { icon: <CardsThree size={28} weight="duotone" />, title: "Q & A → Statements", body: "Paste a stack of multiple-choice questions. Our AI converts each into a clean declarative answer you can actually memorize." },
              { icon: <BookOpen size={28} weight="duotone" />, title: "Script chunking", body: "Paste, upload PDF / Word, or import your script. Pick short, medium, or long chunks — we split preserving your exact wording." },
              { icon: <Timer size={28} weight="duotone" />, title: "Match how you learn", body: "Exact mode for terminology and figures. Concept mode accepts rephrasing — say it your way and still pass." },
            ].map((f, i) => (
              <div key={i} className="bg-white p-8 md:p-10" data-testid={`feature-card-${i}`}>
                <div className="text-[color:var(--oxford)]">{f.icon}</div>
                <h3 className="font-serif text-2xl mt-6">{f.title}</h3>
                <p className="text-[color:var(--ink-muted)] mt-3 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="bg-white border-t rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-16">
          <div>
            <div className="eyebrow mb-4">For whom</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Students preparing for exams.<br/>
              Professionals preparing to speak.
            </h2>
            <p className="mt-6 text-[color:var(--ink-muted)] leading-relaxed max-w-lg">
              Whether you&apos;re running flashcards against a 300-question board exam or drilling
              the opening minute of a Monday keynote, Ekkol works the same simple way:
              listen, repeat, advance.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="border rule p-8">
              <GraduationCap size={28} weight="duotone" />
              <div className="font-serif text-2xl mt-6">Exam prep</div>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">Turn MCQ banks into memorable statements.</p>
            </div>
            <div className="border rule p-8">
              <Microphone size={28} weight="duotone" />
              <div className="font-serif text-2xl mt-6">Speeches</div>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">Rehearse scripts chunk-by-chunk.</p>
            </div>
            <div className="border rule p-8">
              <Waveform size={28} weight="duotone" />
              <div className="font-serif text-2xl mt-6">Sales pitches</div>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">Internalize your product narrative.</p>
            </div>
            <div className="border rule p-8">
              <CardsThree size={28} weight="duotone" />
              <div className="font-serif text-2xl mt-6">Key data</div>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">Drill numbers, names, and definitions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t rule">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            The next thing you memorize<br/>shouldn&apos;t feel like a chore.
          </h2>
          <div className="mt-10">
            <Link to="/login" className="btn-primary" data-testid="cta-bottom">
              Start studying <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
          <div className="mt-6 text-xs font-mono uppercase tracking-[0.2em] text-[color:var(--ink-faint)]">
            Free forever tier · No credit card
          </div>
        </div>
      </section>

      <footer className="border-t rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-[color:var(--ink-muted)] font-mono">
          <span>© {new Date().getFullYear()} EKKOL · MADE FOR FOCUSED MINDS</span>
          <span>VOICE · REPETITION · RETENTION</span>
        </div>
      </footer>
    </div>
  );
}
